import { useState, useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { Table } from "react-bootstrap";
import { niceDateTime, niceNumber, classNames } from "@/lib/util";
import { ModelData, Operation } from "./modelDetails.lib";
import DeviationBar from "@/modules/shared/dashboard/DeviationBar";
import OrderTableProductionModal from "@/modules/shared/orderProduction/OrderTableProductionModal";
import Tooltip from "@/modules/shared/ui/Tooltip";
import warningIcon from "@/img/warning-new.svg";

const SAMPLE_SIZE = 10;
const BASE_RATIO = 1;

type OperationWithEstimates = Operation & {
    estimationModelMin: number;
    estimationNormativeMin: number;
};

type Props = {
    model: ModelData;
};

function hasValidActualDuration(operation: Operation): boolean {
    return operation.actualDateStartTs != null && operation.actualDateEndTs != null && operation.actualDurationH > 0;
}

function formatSignedDelta(delta_min: number, use_hours: boolean): string {
    if (delta_min === 0) {
        return "–";
    }
    const display = use_hours ? delta_min / 60 : delta_min;
    return `${display > 0 ? "+" : ""}${niceNumber(display, 2)}`;
}

/**
 * Table shows the latest operations history with estimates and errors.
 * Estimates come from server evaluateComplex (same path as model-evaluation KPI).
 */
const LatestOperationsHistoryTable = ({ model }: Props) => {
    const { t } = useLingui();
    const [selectedOperationUuid, setSelectedOperationUuiId] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [useHours, setUseHours] = useState(false);

    const operations = model.latestOperations.length > 0 ? model.latestOperations.slice(0, SAMPLE_SIZE) : [];
    const toDisplay = (duration_min: number) => (useHours ? duration_min / 60 : duration_min);
    const excluded_from_totals_tooltip = t`Excluded from totals: this work order has no actual duration, so model and normative estimates are not included in the totals.`;

    const operationsWithEstimates = useMemo(() => {
        return operations.map<OperationWithEstimates>(operation => ({
            ...operation,
            estimationModelMin: operation.durationModelH * 60,
            estimationNormativeMin: operation.durationNormativeH * 60,
        }));
    }, [operations]);

    const maxRatio = useMemo(() => {
        let max = BASE_RATIO;
        for (const op of operationsWithEstimates) {
            if (!hasValidActualDuration(op)) {
                continue;
            }

            const actualDurationMin = op.actualDurationH * 60;
            max = Math.max(
                max,
                op.estimationModelMin / actualDurationMin,
                op.estimationNormativeMin / actualDurationMin
            );
        }

        return max;
    }, [operationsWithEstimates]);

    const totals = useMemo(() => {
        return operationsWithEstimates.reduce(
            (acc, operation) => {
                acc.quantity += operation.quantity;
                if (hasValidActualDuration(operation)) {
                    acc.actual_duration_min += operation.actualDurationH * 60;
                    acc.estimation_model_min += operation.estimationModelMin;
                    acc.estimation_normative_min += operation.estimationNormativeMin;
                } else {
                    acc.excluded_estimate_count += 1;
                }
                return acc;
            },
            {
                quantity: 0,
                actual_duration_min: 0,
                estimation_model_min: 0,
                estimation_normative_min: 0,
                excluded_estimate_count: 0,
            }
        );
    }, [operationsWithEstimates]);

    const selectedOperation = operations.find(operation => operation.operationUuid === selectedOperationUuid);

    const renderExcludedFromTotalsIndicator = () => (
        <Tooltip content={excluded_from_totals_tooltip}>
            <span
                className="excluded-from-totals-indicator d-inline-flex align-items-center"
                aria-label={excluded_from_totals_tooltip}
            >
                <img src={warningIcon} alt="" width={14} height={14} />
            </span>
        </Tooltip>
    );

    return (
        <>
            {selectedOperation && selectedOperationUuid && (
                <OrderTableProductionModal
                    show={showModal}
                    selected_order_uuid={selectedOperationUuid ?? ""}
                    selected_order_external_id={selectedOperation.orderExternalId}
                    process_num={"0"}
                    onShowOrHideOrderModal={() => {
                        setShowModal(!showModal);
                    }}
                    onOrderSave={() => {}}
                    show_columns={[
                        "process_num",
                        "title",
                        "quantity_produced",
                        "quantity_scrap",
                        "quantity_total",
                        "output_stock",
                        "status",
                        "time_start",
                        "time_end",
                        "skip_sim",
                        "ignore_on_planning_board",
                    ]}
                />
            )}
            <div className="card p-0">
                <div className="card-title card-title-border-bottom p-3 d-flex justify-content-between align-items-center gap-3 flex-wrap">
                    <span>{t`Latest Work-Orders History (max 10)`}</span>
                    <div className="d-flex align-items-center gap-3" role="group" aria-label={t`Display duration in`}>
                        <span className="card-label mb-0">{t`Display Duration in`}:</span>
                        <label className="d-flex align-items-center gap-2 mb-0">
                            <input
                                type="radio"
                                name="latest-operations-duration-unit"
                                value="minutes"
                                checked={!useHours}
                                onChange={() => setUseHours(false)}
                            />
                            {t`Minutes`}
                        </label>
                        <label className="d-flex align-items-center gap-2 mb-0">
                            <input
                                type="radio"
                                name="latest-operations-duration-unit"
                                value="hours"
                                checked={useHours}
                                onChange={() => setUseHours(true)}
                            />
                            {t`Hours`}
                        </label>
                    </div>
                </div>
                <div className="card-content">
                    {operations.length === 0 ? (
                        <div className="p-3 text-center text-muted">{t`No work orders found for this model.`}</div>
                    ) : (
                        <div className="table-responsive">
                            <Table size="sm" responsive className="dashboard-table">
                                <thead>
                                    <tr className="text-center">
                                        <th className="text-left">{t`Order ID`}</th>
                                        <th className="text-left">{t`Start`}</th>
                                        <th className="text-left">{t`End`}</th>
                                        <th className="text-right">{t`QTY`}</th>
                                        <th className="text-right">
                                            {useHours ? t`Actual Dur. (h)` : t`Actual Dur. (min)`}
                                        </th>
                                        <th className="text-right">
                                            {useHours ? t`Norm BreakTime (h)` : t`Norm BreakTime (min)`}
                                        </th>
                                        <th className="text-right">
                                            {useHours ? t`Model Est. (h)` : t`Model Est. (min)`}
                                        </th>
                                        <th className="text-right">
                                            {useHours ? t`Norm Est. (h)` : t`Norm Est. (min)`}
                                        </th>
                                        <th className="text-left">{t`Error analysis`}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {operationsWithEstimates.map((operation, index) => {
                                        const hasStart = operation.actualDateStartTs !== null;
                                        const hasEnd = operation.actualDateEndTs !== null;
                                        const hasBothTimes = hasStart && hasEnd;
                                        const includeInEstimateTotals = hasValidActualDuration(operation);

                                        const actualDurationMin = operation.actualDurationH * 60;
                                        const modelDeltaMin = includeInEstimateTotals
                                            ? operation.estimationModelMin - actualDurationMin
                                            : 0;
                                        const normativeDeltaMin = includeInEstimateTotals
                                            ? operation.estimationNormativeMin - actualDurationMin
                                            : 0;

                                        const dateStart =
                                            operation.actualDateStartTs !== null
                                                ? niceDateTime(new Date(operation.actualDateStartTs))
                                                : "–";
                                        const dateEnd =
                                            operation.actualDateEndTs !== null
                                                ? niceDateTime(new Date(operation.actualDateEndTs))
                                                : "–";
                                        const duration = hasBothTimes
                                            ? niceNumber(toDisplay(actualDurationMin), 2, 2)
                                            : "–";

                                        const modelRatio = includeInEstimateTotals
                                            ? operation.estimationModelMin / actualDurationMin
                                            : 0;
                                        const normativeRatio = includeInEstimateTotals
                                            ? operation.estimationNormativeMin / actualDurationMin
                                            : 0;

                                        return (
                                            <tr
                                                key={index}
                                                className={classNames({
                                                    "row--excluded-from-estimate-totals": !includeInEstimateTotals,
                                                })}
                                            >
                                                <td>
                                                    <a
                                                        href="#"
                                                        onClick={() => {
                                                            setSelectedOperationUuiId(operation.operationUuid);
                                                            setShowModal(true);
                                                        }}
                                                    >
                                                        {operation.orderExternalId}
                                                    </a>
                                                    {!includeInEstimateTotals && (
                                                        <span className="ml-2">
                                                            {renderExcludedFromTotalsIndicator()}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{dateStart}</td>
                                                <td>{dateEnd}</td>
                                                <td className="text-right font-weight-bold">
                                                    {niceNumber(operation.quantity, 2)}
                                                </td>
                                                <td className="text-right">{duration}</td>
                                                <td className="text-right column--norm-break-time">
                                                    {niceNumber(toDisplay(operation.breakTimeMin), 2, 2)}
                                                </td>
                                                <td
                                                    className={classNames("column--model-estimation", {
                                                        "text-muted": !includeInEstimateTotals,
                                                    })}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center gap-2">
                                                        <span className="flex-grow-1 text-right">
                                                            {niceNumber(toDisplay(operation.estimationModelMin), 2, 2)}
                                                        </span>
                                                        {includeInEstimateTotals ? (
                                                            <span
                                                                className={classNames("badge delta-badge flex-grow-1", {
                                                                    "variant-success": modelDeltaMin > 0,
                                                                    "variant-danger": modelDeltaMin < 0,
                                                                    "variant-neutral": modelDeltaMin === 0,
                                                                })}
                                                            >
                                                                {formatSignedDelta(modelDeltaMin, useHours)}
                                                            </span>
                                                        ) : (
                                                            <span className="badge delta-badge flex-grow-1 variant-neutral">
                                                                –
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td
                                                    className={classNames("column--normative-estimation", {
                                                        "text-muted": !includeInEstimateTotals,
                                                    })}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="flex-grow-1 text-right">
                                                            {niceNumber(
                                                                toDisplay(operation.estimationNormativeMin),
                                                                2,
                                                                2
                                                            )}
                                                        </span>
                                                        {includeInEstimateTotals ? (
                                                            <span
                                                                className={classNames("badge delta-badge flex-grow-1", {
                                                                    "variant-success": normativeDeltaMin > 0,
                                                                    "variant-danger": normativeDeltaMin < 0,
                                                                    "variant-neutral": normativeDeltaMin === 0,
                                                                })}
                                                            >
                                                                {formatSignedDelta(normativeDeltaMin, useHours)}
                                                            </span>
                                                        ) : (
                                                            <span className="badge delta-badge flex-grow-1 variant-neutral">
                                                                –
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {includeInEstimateTotals ? (
                                                        <div className="error-bars-container">
                                                            <span>{t`M`}:</span>
                                                            <DeviationBar
                                                                value={modelRatio}
                                                                baseValue={BASE_RATIO}
                                                                maxValue={maxRatio}
                                                            />
                                                            <span className="text-right">
                                                                {niceNumber(modelRatio * 100, 1, 1)}%
                                                            </span>
                                                            <span>{t`N`}:</span>
                                                            <DeviationBar
                                                                value={normativeRatio}
                                                                baseValue={BASE_RATIO}
                                                                maxValue={maxRatio}
                                                            />
                                                            <span className="text-right">
                                                                {niceNumber(normativeRatio * 100, 1, 1)}%
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        "–"
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="font-weight-bold">
                                        <td>
                                            <span className="d-inline-flex align-items-center gap-2">
                                                {t`Totals`}
                                                {totals.excluded_estimate_count > 0 && (
                                                    <Tooltip
                                                        content={t`${totals.excluded_estimate_count} work order(s) without actual duration were excluded from model and normative estimate totals.`}
                                                    >
                                                        <span className="excluded-from-totals-indicator d-inline-flex align-items-center">
                                                            <img src={warningIcon} alt="" width={14} height={14} />
                                                            <span className="ml-1 text-muted font-weight-normal">
                                                                (−{totals.excluded_estimate_count})
                                                            </span>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </span>
                                        </td>
                                        <td></td>
                                        <td></td>
                                        <td className="text-right">{niceNumber(totals.quantity, 2)}</td>
                                        <td className="text-right">
                                            {niceNumber(toDisplay(totals.actual_duration_min), 2, 2)}
                                        </td>
                                        <td className="text-right column--norm-break-time"></td>
                                        <td className="text-right column--model-estimation">
                                            {niceNumber(toDisplay(totals.estimation_model_min), 2, 2)}
                                        </td>
                                        <td className="text-right column--normative-estimation">
                                            {niceNumber(toDisplay(totals.estimation_normative_min), 2, 2)}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default LatestOperationsHistoryTable;
